import { onUnmounted, readonly, ref, watch, type Ref } from 'vue';

import { useAuthStore } from '@/entities/auth/@x/messenger';
import { EchoClient } from '@/shared/websocket';

/** Member presence-канала операторов (id + имя). Форма фиксирована бэкендом. */
interface PresenceOperator {
  id: string;
  name: string;
}

export interface UseOperatorsPresenceReturn {
  /** Реактивный набор id онлайн-операторов организации. */
  onlineOperatorIds: Readonly<Ref<Set<string>>>;
}

const buildChannelName = (organizationId: string): string => `messenger.presence.${organizationId}`;

/**
 * Presence онлайн-операторов организации.
 *
 * Подписка на `messenger.presence.{organizationId}` (id — из текущей
 * организации auth-store, тот же источник, что у приватного канала мессенджера).
 * Синглтон на страницу: поднимать один раз (MessengerPage) и провайдить/передавать
 * потребителям, чтобы не плодить подписки. `leave` в onUnmounted.
 */
export const useOperatorsPresence = (): UseOperatorsPresenceReturn => {
  const authStore = useAuthStore();

  const onlineOperatorIds = ref<Set<string>>(new Set());
  let currentChannelName: string | null = null;

  const subscribe = (organizationId: string): void => {
    currentChannelName = buildChannelName(organizationId);

    try {
      EchoClient.join(currentChannelName)
        .here((members: PresenceOperator[]) => {
          onlineOperatorIds.value = new Set(members.map((member) => member.id));
        })
        .joining((member: PresenceOperator) => {
          const next = new Set(onlineOperatorIds.value);
          next.add(member.id);
          onlineOperatorIds.value = next;
        })
        .leaving((member: PresenceOperator) => {
          const next = new Set(onlineOperatorIds.value);
          next.delete(member.id);
          onlineOperatorIds.value = next;
        })
        .error((error: unknown) => {
          console.warn('[useOperatorsPresence] Ошибка подписки на presence-канал операторов', error);
        });
    } catch (error) {
      console.warn('[useOperatorsPresence] Не удалось подписаться на presence-канал операторов', error);
    }
  };

  const unsubscribe = (): void => {
    if (currentChannelName) {
      EchoClient.leave(currentChannelName);
      currentChannelName = null;
    }
    onlineOperatorIds.value = new Set();
  };

  const initialOrganizationId = authStore.currentOrganization?.id;
  if (initialOrganizationId && !authStore.isImpersonating) {
    subscribe(initialOrganizationId);
  }

  // Переподписка при смене организации; в режиме impersonation presence
  // намеренно выключен, при этом приватные каналы мессенджера остаются доступны.
  watch(
    () => [authStore.currentOrganization?.id, authStore.isImpersonating] as const,
    ([organizationId, isImpersonating], [previousId, wasImpersonating]) => {
      if (organizationId === previousId && isImpersonating === wasImpersonating) {
        return;
      }

      unsubscribe();

      if (organizationId && !isImpersonating) {
        subscribe(organizationId);
      }
    },
  );

  onUnmounted(unsubscribe);

  return { onlineOperatorIds: readonly(onlineOperatorIds) as Readonly<Ref<Set<string>>> };
};
